import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import EditSvg from 'images/v5/icons/edit-02.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import { Cell, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import Select from 'layout/molecules/Select/Select';
import { openModal } from 'store/layoutSlice';
import { fetchProductContinents, selectProductContinents, selectProductContinentsStatus, } from 'store/parameters/productContinentsSlice';
import { fetchProductCountries, removeProductCountry, resetProductCountries, selectProductCountries, selectProductCountriesContinentFilter, selectProductCountriesErrors, selectProductCountriesPageInfo, selectProductCountriesRefetchFlags, selectProductCountriesStatus, setContinentFilter, } from 'store/parameters/productCountriesSlice';
import ParametersTable from '../shared/ParametersTable';
function Countries() {
    const dispatch = useDispatch();
    const { t } = useTranslation(['specification', 'common']);
    const countries = useSelector(selectProductCountries);
    const pageInfo = useSelector(selectProductCountriesPageInfo);
    const status = useSelector(selectProductCountriesStatus);
    const errors = useSelector(selectProductCountriesErrors);
    const refetchFlags = useSelector(selectProductCountriesRefetchFlags);
    const continentFilter = useSelector(selectProductCountriesContinentFilter);
    const continents = useSelector(selectProductContinents);
    const continentsStatus = useSelector(selectProductContinentsStatus);
    useEffect(() => {
        if (continentsStatus.fetch === 'idle') {
            dispatch(fetchProductContinents());
        }
    }, [dispatch, continentsStatus.fetch]);
    useEffect(() => {
        if (status.fetch === 'idle' || refetchFlags.fetch) {
            dispatch(fetchProductCountries());
        }
    }, [dispatch, status.fetch, refetchFlags.fetch]);
    useEffect(() => {
        return () => {
            dispatch(resetProductCountries());
        };
    }, [dispatch]);
    const fetchMore = () => {
        if (pageInfo.hasNextPage && status.fetch !== 'loading') {
            dispatch(fetchProductCountries());
        }
    };
    const handleContinentFilterChange = (value) => {
        dispatch(setContinentFilter(value || null));
        dispatch(fetchProductCountries());
    };
    const handleDelete = (countryId) => {
        dispatch(removeProductCountry(countryId));
    };
    const continentOptions = [
        { value: '', label: t('specification:filter_by_continent') },
        ...continents.map((c) => ({ value: c.id, label: c.name })),
    ];
    if (status.fetch === 'loading' || status.fetch === 'idle') {
        return React.createElement(Loader, null);
    }
    if (status.fetch === 'failed') {
        return React.createElement("div", null, errors.fetch);
    }
    return (React.createElement(Grid, { gap: 24 },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
            React.createElement(Select, { options: continentOptions, value: continentFilter || '', onChange: handleContinentFilterChange, placeholder: t('specification:filter_by_continent'), loading: continentsStatus.fetch === 'loading', fullWidth: false }),
            React.createElement(Button, { color: "primary", onClick: () => dispatch(openModal({ name: 'countryCreator' })) }, t('specification:add_country'))),
        countries.length === 0 ? (React.createElement(EmptyState, null)) : (React.createElement(ParametersTable, { columns: [
                ['320px', '1fr'],
                ['200px', 'auto'],
                ['72px', 'auto'],
            ], headers: [
                { content: t('common:name') },
                { content: t('specification:continent') },
                { content: null },
            ], dataLength: countries.length, hasMore: pageInfo.hasNextPage, fetchMore: fetchMore }, countries.map((country) => {
            var _a, _b;
            return (React.createElement(Row, { key: country.id },
                React.createElement(Cell, null,
                    React.createElement("span", null, country.name)),
                React.createElement(Cell, null,
                    React.createElement("span", null, (_b = (_a = country.continent) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '—')),
                React.createElement(Cell, null,
                    React.createElement(Dropdown, null,
                        country.canBeUpdated !== false && (React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(EditSvg, null), onClick: () => dispatch(openModal({ name: 'countryEditor', id: country.id })) }, t('common:edit'))),
                        country.canBeRemoved !== false && (React.createElement(Button, { align: "left", theme: "ghost", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => handleDelete(country.id) }, t('common:delete')))))));
        })))));
}
export default memo(Countries);
